import { ApolloError, useQuery } from '@apollo/client';

import { GetInstoreCartDocument } from 'src/gql/graphql';
import type { GetInstoreCartQuery } from 'src/gql/graphql';
import { getLogger } from 'src/utils/datadog-logger';
import { LOG_NAMESPACES } from 'src/payments/constants';
import { InstoreCartResponse } from './types';

const logger = getLogger(LOG_NAMESPACES.PBB_INSTORE_CART);

export type InstoreCartQuery = {
  data?: InstoreCartResponse;
  loading?: boolean;
  error?: ApolloError;
  startPolling: (pollInterval: number) => void;
  stopPolling: () => void;
};

function instoreCartResponseAdapter(response?: GetInstoreCartQuery): InstoreCartResponse | undefined {
  const instoreCart = response?.getInstoreCart?.instoreCartData?.instoreCart;
  const instoreCartData = response?.getInstoreCart?.instoreCartData;

  if (!instoreCart || !instoreCartData) {
    return undefined;
  }

  const { retailerId: dispensaryId, tippingDisabled } = instoreCartData;

  return {
    dispensaryId,
    currency: instoreCart.currency,
    discounts: instoreCart.discounts ?? [],
    fees: instoreCart.fees ?? [],
    items: instoreCart.items,
    subtotalCents: instoreCart.subtotalCents,
    taxes: instoreCart.taxes,
    tipCents: instoreCart.tipCents,
    totalCents: instoreCart.totalCents,
    id: instoreCart.id,
    tippingDisabled,
  };
}

export default function useGetInstoreCart({
  skip,
  cartToken,
}: {
  skip: boolean;
  cartToken: string | null | undefined;
}): InstoreCartQuery {
  const { data, loading, error, startPolling, stopPolling } = useQuery(GetInstoreCartDocument, {
    variables: {
      cartToken,
    },
    skip,
  });

  if (error) {
    logger.error('useGetInstoreCartQuery Error', { error, cartToken: cartToken ?? 'unknown' });
  }

  return { data: instoreCartResponseAdapter(data), loading, error, startPolling, stopPolling };
}
